import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

export default function EventForm({ initialData = {}, onSubmit, loading, submitLabel = 'Save Event' }) {
  const [formData, setFormData] = useState({
    slug: initialData.slug || '',
    name: initialData.name || '',
    city: initialData.city || '',
    citySlug: initialData.citySlug || '',
    venueName: initialData.venueName || '',
    venueSlug: initialData.venueSlug || '',
    dates: {
      start: initialData.dates?.start || '',
      end: initialData.dates?.end || '',
      display: initialData.dates?.display || ''
    },
    organiserName: initialData.organiserName || '',
    organiserWebsite: initialData.organiserWebsite || '',
    officialUrl: initialData.officialUrl || '',
    shortDescription: initialData.shortDescription || '',
    longDescription: initialData.longDescription || '',
    sectorTags: initialData.sectorTags || [],
    status: initialData.status || 'upcoming',
    ownerType: initialData.ownerType || 'internal',
    ownerEmail: initialData.ownerEmail || '',
    seriesKey: initialData.seriesKey || ''
  });

  const [tagInput, setTagInput] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('dates.')) {
      const dateField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        dates: { ...prev.dates, [dateField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-generate slugs if needed
    if (name === 'city') {
      const slugified = value.toLowerCase().replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, citySlug: slugified }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.sectorTags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        sectorTags: [...prev.sectorTags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      sectorTags: prev.sectorTags.filter(t => t !== tag)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Details */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Details</CardTitle>
          <CardDescription>Event identification and naming</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Event Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="event-name-input"
              />
            </div>
            <div>
              <Label htmlFor="slug">Slug (URL) *</Label>
              <Input
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                placeholder="event-name-2026"
                data-testid="event-slug-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used in URL: /events/city/venue/{formData.slug}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Location */}
      <Card>
        <CardHeader>
          <CardTitle>Location</CardTitle>
          <CardDescription>City and venue information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">City *</Label>
              <Select value={formData.city} onValueChange={(value) => handleSelectChange('city', value)} required>
                <SelectTrigger data-testid="city-select">
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="London">London</SelectItem>
                  <SelectItem value="Birmingham">Birmingham</SelectItem>
                  <SelectItem value="Manchester">Manchester</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="citySlug">City Slug *</Label>
              <Input
                id="citySlug"
                name="citySlug"
                value={formData.citySlug}
                onChange={handleChange}
                required
                placeholder="london"
                data-testid="city-slug-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="venueName">Venue Name *</Label>
              <Input
                id="venueName"
                name="venueName"
                value={formData.venueName}
                onChange={handleChange}
                required
                data-testid="venue-name-input"
              />
            </div>
            <div>
              <Label htmlFor="venueSlug">Venue Slug *</Label>
              <Input
                id="venueSlug"
                name="venueSlug"
                value={formData.venueSlug}
                onChange={handleChange}
                required
                placeholder="excel-london"
                data-testid="venue-slug-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dates */}
      <Card>
        <CardHeader>
          <CardTitle>Dates</CardTitle>
          <CardDescription>Event schedule</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="dates.start">Start Date *</Label>
              <Input
                type="date"
                id="dates.start"
                name="dates.start"
                value={formData.dates.start}
                onChange={handleChange}
                required
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label htmlFor="dates.end">End Date *</Label>
              <Input
                type="date"
                id="dates.end"
                name="dates.end"
                value={formData.dates.end}
                onChange={handleChange}
                required
                data-testid="end-date-input"
              />
            </div>
            <div>
              <Label htmlFor="dates.display">Display String *</Label>
              <Input
                id="dates.display"
                name="dates.display"
                value={formData.dates.display}
                onChange={handleChange}
                required
                placeholder="11–12 November 2026"
                data-testid="display-date-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle>Descriptions</CardTitle>
          <CardDescription>Event content and marketing copy</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="shortDescription">Short Description *</Label>
            <Textarea
              id="shortDescription"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              required
              rows={2}
              placeholder="1-2 sentence teaser"
              data-testid="short-description-input"
            />
          </div>
          <div>
            <Label htmlFor="longDescription">Long Description *</Label>
            <Textarea
              id="longDescription"
              name="longDescription"
              value={formData.longDescription}
              onChange={handleChange}
              required
              rows={6}
              placeholder="Full marketing copy"
              data-testid="long-description-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Organiser & URLs */}
      <Card>
        <CardHeader>
          <CardTitle>Organiser & URLs</CardTitle>
          <CardDescription>Event organiser and external links reviewed before a listing can go live</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="organiserName">Organiser Name *</Label>
              <Input
                id="organiserName"
                name="organiserName"
                value={formData.organiserName}
                onChange={handleChange}
                required
                data-testid="organiser-name-input"
              />
            </div>
            <div>
              <Label htmlFor="organiserWebsite">Organiser Website</Label>
              <Input
                type="url"
                id="organiserWebsite"
                name="organiserWebsite"
                value={formData.organiserWebsite}
                onChange={handleChange}
                placeholder="https://"
                data-testid="organiser-website-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="officialUrl">Official Event URL</Label>
            <Input
              type="url"
              id="officialUrl"
              name="officialUrl"
              value={formData.officialUrl}
              onChange={handleChange}
              placeholder="https://"
              data-testid="official-url-input"
            />
          </div>
          <p className="text-xs leading-5 text-gray-500" data-testid="event-form-verification-note">
            New listings are checked against organiser details and official source pages. If the source page does not clearly match the event, publication may be blocked pending review.
          </p>
        </CardContent>
      </Card>

      {/* Tags & Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Tags & Metadata</CardTitle>
          <CardDescription>Categorization and metadata</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Sector Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add tag and press Enter"
                data-testid="tag-input"
              />
              <Button type="button" onClick={handleAddTag} variant="outline">Add</Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.sectorTags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => handleRemoveTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => handleSelectChange('status', value)}>
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="past">Past</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="ownerType">Owner Type</Label>
              <Select value={formData.ownerType} onValueChange={(value) => handleSelectChange('ownerType', value)}>
                <SelectTrigger data-testid="owner-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="organiser">Organiser</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="seriesKey">Series Key</Label>
              <Input
                id="seriesKey"
                name="seriesKey"
                value={formData.seriesKey}
                onChange={handleChange}
                placeholder="event-series"
                data-testid="series-key-input"
              />
            </div>
          </div>

          {formData.ownerType === 'organiser' && (
            <div>
              <Label htmlFor="ownerEmail">Owner Email</Label>
              <Input
                type="email"
                id="ownerEmail"
                name="ownerEmail"
                value={formData.ownerEmail}
                onChange={handleChange}
                placeholder="organiser@example.com"
                data-testid="owner-email-input"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-end space-x-4">
        <Button type="submit" disabled={loading} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-event-button">
          {loading ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </form>
  );
}
